import React from 'react'
import { List, ListSubheader, Box, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import CategoryItemList from './CategoryItemList'
import { useUserRoles } from 'src/Hooks/user'
import type { Category, CategoryItem } from './types'

export { CategoryItemType } from './types'
export type { Category, CategoryItem } from './types'

const useStyles = makeStyles()(theme => ({
  list: {
    flex: 1,
  },
  categoryHeader: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    color: theme.palette.grey.grey03,
    fontSize: theme.typography.headerCategoryTitle.fontSize,
    fontWeight: theme.typography.headerCategoryTitle.fontWeight,
    lineHeight: theme.typography.subtitle2.lineHeight,
    paddingLeft: theme.spacing(4),
  },
  titleWordBreak: {
    wordBreak: 'break-word',
  },
}))

export interface CategoriesProps {
  categories?: Category[]
  onClose?: () => void
  handleCategoryListItemClick?: (item: CategoryItem, items?: Category[]) => void
  collapsed?: boolean
  collapsedListClass?: string
  categoriesClasses?: string
  categoryItemClasses?: string
  showActiveIndicator?: boolean
}

const Categories: React.FC<CategoriesProps> = ({
  categories,
  onClose,
  handleCategoryListItemClick,
  collapsed,
  collapsedListClass,
  categoriesClasses,
  categoryItemClasses,
  showActiveIndicator,
}) => {
  const { classes, cx } = useStyles()
  const { isPartner, isInstaller, isServiceUser } = useUserRoles()
  const isEligibleUser = isPartner || isInstaller || isServiceUser
  if (!categories || categories.length === 0) {
    return null
  }

  return (
    <>
      {categories.map((category, index) => {
        const renderCategoryName = (
          <Box className={classes.titleWordBreak} id={'category-name'}>
            <Typography variant="caption">{collapsed && category.nameCollapsed ? category.nameCollapsed : category.name}</Typography>
          </Box>
        )

        const renderSubheader = (
          <Box id={'category-subheader'}>
            <Typography variant="caption">{collapsed && category.subheaderCollapsed ? category.subheaderCollapsed : category.subheader}</Typography>
          </Box>
        )

        const renderTertiaryheader = (
          <Box id={'category-tertiaryheader'}>
            <Typography variant="caption">
              {collapsed && category.tertiaryheaderCollapsed ? category.tertiaryheaderCollapsed : category.tertiaryheader}
            </Typography>
          </Box>
        )

        const subheader = (
          <ListSubheader
            component="div"
            className={cx(classes.categoryHeader, collapsed && collapsedListClass, categoriesClasses)}
            disableSticky={true}
          >
            <>
              {renderCategoryName}
              {category.subheader && renderSubheader}
              {category.tertiaryheader && isEligibleUser && renderTertiaryheader}
            </>
          </ListSubheader>
        )

        return (
          <List
            disablePadding
            sx={category.sx}
            className={cx(classes.list)}
            subheader={category.name ? subheader : undefined}
            key={`list-${category.name}-${index}`}
          >
            <CategoryItemList
              categoryItems={category.items}
              onClose={onClose}
              handleCategoryListItemClick={handleCategoryListItemClick}
              collapsed={collapsed}
              categoryItemClasses={categoryItemClasses}
              showActiveIndicator={showActiveIndicator}
            />
          </List>
        )
      })}
    </>
  )
}

export default Categories
